/**
 * Human-readable text for an API failure.
 *
 * handleApiError puts `response.data.detail` into Error.message, but a FastAPI
 * 422 detail is an ARRAY of {loc, msg} objects — which stringifies to
 * "[object Object]". This flattens either shape into something worth showing.
 */
export function errorDetailText(error: unknown): string | null {
  if (!error) return null;
  const anyErr = error as { data?: any; message?: unknown; status?: number };
  const detail = anyErr?.data?.detail ?? anyErr?.message;
  if (typeof detail === 'string') return detail.trim() || null;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        if (d && typeof d.msg === 'string') {
          const loc = Array.isArray(d.loc) ? d.loc.filter((p: unknown) => p !== 'query' && p !== 'body').join('.') : '';
          return loc ? `${loc}: ${d.msg}` : d.msg;
        }
        return null;
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  if (detail && typeof detail === 'object' && typeof (detail as any).message === 'string') {
    return (detail as any).message;
  }
  return null;
}

export function errorStatus(error: unknown): number | null {
  const s = (error as { status?: unknown })?.status;
  return typeof s === 'number' ? s : null;
}

/** Machine-readable `code` from a `{detail, code}` error body (or detail.code). */
export function errorCode(error: unknown): string | null {
  const data = (error as { data?: any })?.data;
  const code = data?.code ?? (data?.detail && typeof data.detail === 'object' ? data.detail.code : undefined);
  return typeof code === 'string' ? code : null;
}
